import { map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { ReactCountryFlag } from 'react-country-flag';

import Modal from 'components/Modal';
import { GEOFENCING_SHOP_IDS } from 'constants/geofencing';
import I18n from 'utils/I18n';
import { cn } from 'utils/Tailwind';

import 'styles/tailwind.css';

// To map to the correct country code for the flag
const convertCountryCode = id => {
  switch (id) {
    case GEOFENCING_SHOP_IDS.UK:
      return 'GB';
    default:
      return id;
  }
};

const GeofencingShopSelector = ({
  isOpen,
  currentShopID,
  alternativeShopID,
  setShopSelection,
  remainingShops,
  modalSettings,
}) => {
  if (!alternativeShopID) {
    return null;
  }

  const currentShopName = I18n.localize(`shops.${currentShopID}.name`);
  const currentShopAriaLabel = I18n.localize('geofencingShopSelector.stayOnCurrentShopAriaLabel', {
    shop: currentShopName,
  });
  const alternativeShopAriaLabel = I18n.localize(
    'geofencingShopSelector.setAlternativeShopAriaLabel',
    {
      shop: I18n.localize(`shops.${alternativeShopID}.name`),
    }
  );

  return (
    <Modal
      isOpen={isOpen}
      className="GeofencingShopSelector__modal !max-w-[600px] !p-0 sm:!min-w-[600px]"
      portalClassName="GeofencingShopSelector__portal"
      onRequestClose={() => setShopSelection(currentShopID)}
      {...modalSettings}
    >
      <div className="GeofencingShopSelector mx-auto px-6 pb-6 pt-14 sm:p-12">
        <h2 className="text-center text-xl font-bold leading-tight md:text-3xl">
          {I18n.localize('geofencingShopSelector.heading')}
        </h2>

        <div className="GeofencingShopSelector__flags my-4 flex justify-center gap-4 text-center md:my-6 md:gap-10 lg:gap-12">
          <CountryOption
            ariaLabel={currentShopAriaLabel}
            shopId={currentShopID}
            setShopSelection={setShopSelection}
          />
          <CountryOption
            ariaLabel={alternativeShopAriaLabel}
            shopId={alternativeShopID}
            setShopSelection={setShopSelection}
          />
        </div>

        <p className="text-center text-sm md:text-base">
          {I18n.localize('geofencingShopSelector.body')}
        </p>
      </div>

      <div className="bg-grey p-6 md:py-8">
        <p className="mb-2 text-center text-sm font-bold uppercase tracking-widest sm:text-base md:text-left">
          {I18n.localize('geofencingShopSelector.otherCountries')}
        </p>
        <ul className="GeofencingShopSelector__other-countries mx-auto grid auto-rows-min grid-cols-8 justify-center gap-1 md:grid-cols-10">
          {map(remainingShops, shop => (
            <li key={shop.id}>
              <RemainingShop setShopSelection={setShopSelection} shop={shop} />
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

const ShopFlag = ({ shopID }) => (
  <ReactCountryFlag
    aria-label={convertCountryCode(shopID)}
    countryCode={convertCountryCode(shopID)}
    svg
    className="border border-solid border-gray-400"
    style={{ width: '100%', height: 'auto', fontSize: '3em', aspectRatio: '4/3' }}
  />
);

const CountryOption = ({ ariaLabel, shopId, setShopSelection }) => (
  <a
    aria-label={ariaLabel}
    className={cn(
      `GeofencingShopSelector__flag GeofencingShopSelector__flag--${shopId}`,
      'aspect-[4/3] w-24 sm:w-32 sm:min-w-32'
    )}
    href="#set-current-shop-selection"
    onClick={event => {
      event.preventDefault();
      setShopSelection(shopId);
    }}
  >
    <ShopFlag shopID={shopId} />
    <span className="mt-2 inline-flex text-sm font-bold tracking-wider">{shopId}</span>
  </a>
);

const RemainingShop = ({ setShopSelection, shop }) => (
  <a
    aria-label={I18n.localize('geofencingShopSelector.setAlternativeShopAriaLabel', {
      shop: I18n.localize(`shops.${shop.id}.name`),
    })}
    className={cn(
      'GeofencingShopSelector__flag',
      `GeofencingShopSelector__flag--${shop.id}`,
      'min-w-full'
    )}
    href="#set-alternative-shop-selection"
    onClick={event => {
      event.preventDefault();
      setShopSelection(shop.id);
    }}
  >
    <ShopFlag shopID={shop.id} />
  </a>
);

RemainingShop.propTypes = {
  setShopSelection: PropTypes.func,
  shop: PropTypes.shape({
    id: PropTypes.string,
  }),
};

ShopFlag.propTypes = {
  shopID: PropTypes.string,
};

CountryOption.propTypes = {
  ariaLabel: PropTypes.string,
  setShopSelection: PropTypes.func,
  shopId: PropTypes.oneOf(Object.values(GEOFENCING_SHOP_IDS)),
};

GeofencingShopSelector.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  alternativeShopID: PropTypes.string,
  currentShopID: PropTypes.string,
  setShopSelection: PropTypes.func.isRequired,
  remainingShops: PropTypes.arrayOf(PropTypes.shape({})),
  modalSettings: PropTypes.shape({}),
};

GeofencingShopSelector.defaultProps = {
  alternativeShopID: '',
  currentShopID: '',
  modalSettings: {},
  remainingShops: [],
};

export default GeofencingShopSelector;
